import type {
  TooltipState,
  TooltipStoreState,
} from '../stores/tooltipStore.types';
import { isAudienceMatch } from './audienceMatching';
import { isConfigEligibleForRoute } from './routeMatching';

export { isAudienceMatch } from './audienceMatching';

export const ONBOARDING_FEATURE_ID = 'onboarding';

export function isFeatureEnabled(
  state: TooltipStoreState,
  feature: string | undefined
): boolean {
  const id = feature ?? ONBOARDING_FEATURE_ID;
  if (id === ONBOARDING_FEATURE_ID) return state.tooltipsEnabled;
  return state.enabledFeatures[id] === true;
}

export function isTooltipCompleted(
  state: TooltipStoreState,
  tooltipId: string
): boolean {
  const tooltip = state.tooltips.get(tooltipId);
  if (!tooltip) return false;

  // For multi-step groups, consider the group completed if any step is dismissed
  const groupId = tooltip.config.multiStepGroupId;
  if (groupId) {
    const stepIds = state.multiStepGroups.get(groupId);
    if (stepIds) {
      // Check if any step in the group is dismissed
      const anyDismissed = stepIds.some((stepId) => {
        const step = state.tooltips.get(stepId);
        return step?.isDismissed ?? false;
      });
      if (anyDismissed) return true;
    }
  }

  // Tooltip is completed if it's dismissed
  if (tooltip.isDismissed) return true;

  // Tooltip is completed if it has reached maxDisplayCount and is not currently open
  const maxDisplayCount = tooltip.config.maxDisplayCount;
  const hasExpiredByMaxDisplays =
    maxDisplayCount !== undefined &&
    tooltip.displayCount >= maxDisplayCount &&
    !tooltip.isOpen;

  return hasExpiredByMaxDisplays;
}

export function findAllReadyTooltipsForRoute(
  state: TooltipStoreState,
  pathname: string
): Array<{
  id: string;
  precedence: number;
  regIndex: number;
}> {
  const now = Date.now();
  const results: Array<{
    id: string;
    precedence: number;
    regIndex: number;
  }> = [];
  const processedGroups = new Set<string>();

  for (const [id, tooltip] of state.tooltips.entries()) {
    // For multi-step groups, only consider the first step (index 0)
    const groupId = tooltip.config.multiStepGroupId;
    if (groupId) {
      const stepIds = state.multiStepGroups.get(groupId);
      if (stepIds && stepIds.length > 0 && stepIds[0] !== id) {
        // Skip non-first steps of multi-step groups
        // eslint-disable-next-line no-continue
        continue;
      }
      if (processedGroups.has(groupId)) {
        // Already processed this group
        // eslint-disable-next-line no-continue
        continue;
      }
      processedGroups.add(groupId);
    }

    const threshold = tooltip.config.showAfterVisitNumber ?? 1;
    const deps = tooltip.config.displayAfterTooltipsIds;
    const pending = state.pendingRequests.get(id);
    const isCoolingDown = Boolean(
      pending && pending.retryAfter && now < pending.retryAfter
    );
    const depsSatisfied =
      !deps || deps.length === 0
        ? true
        : deps.every((depId) => isTooltipCompleted(state, depId));

    const ready =
      isFeatureEnabled(state, tooltip.config.feature) &&
      !tooltip.isDismissed &&
      !tooltip.isOpen &&
      !tooltip.config.manualOpenOnly &&
      isConfigEligibleForRoute(tooltip.config.routes, pathname) &&
      tooltip.visitCount >= threshold &&
      !(
        tooltip.config.maxDisplayCount !== undefined &&
        tooltip.displayCount >= tooltip.config.maxDisplayCount
      ) &&
      depsSatisfied &&
      !isCoolingDown &&
      isAudienceMatch(tooltip.config.audience, state.audienceContext);

    if (ready) {
      const precedence = tooltip.config.precedence ?? 0;
      const regIndex =
        state.registrationIndexById.get(id) ?? Number.MAX_SAFE_INTEGER;
      results.push({
        id,
        precedence,
        regIndex,
      });
    }
  }

  results.sort((a, b) => {
    if (a.precedence !== b.precedence) return a.precedence - b.precedence;
    return a.regIndex - b.regIndex;
  });

  return results;
}

export function selectIsEligible(
  state: TooltipStoreState,
  t: TooltipState,
  { bypassEligibility = false }: { bypassEligibility?: boolean } = {}
): boolean {
  if (!isFeatureEnabled(state, t.config.feature)) {
    return false;
  }
  if (t.isDismissed) {
    return false;
  }
  if (!bypassEligibility) {
    const needed = t.config.showAfterVisitNumber ?? 1;
    if (t.visitCount < needed) {
      return false;
    }
  }
  if (
    t.config.maxDisplayCount !== undefined &&
    t.displayCount >= t.config.maxDisplayCount
  ) {
    return false;
  }
  if (!isConfigEligibleForRoute(t.config.routes, state.currentPathname)) {
    return false;
  }
  // Check if all dependencies are completed
  if (
    t.config.displayAfterTooltipsIds &&
    t.config.displayAfterTooltipsIds.length > 0
  ) {
    if (
      !t.config.displayAfterTooltipsIds.every((depId) =>
        isTooltipCompleted(state, depId)
      )
    ) {
      return false;
    }
  }
  if (!isAudienceMatch(t.config.audience, state.audienceContext)) {
    return false;
  }
  return true;
}

export function areAllTooltipsDismissedIn(state: TooltipStoreState): boolean {
  if (state.tooltips.size === 0) return false;
  return Array.from(state.tooltips.keys()).every((tooltipId) =>
    isTooltipCompleted(state, tooltipId)
  );
}

export function applySelection(state: TooltipStoreState): TooltipStoreState {
  if (state.activeTooltipId) {
    return state;
  }
  if (state.pendingRequests.size === 0) {
    return state;
  }

  // Choose by precedence, then earlier registration; DOM presence is handled by renderer confirm/release
  let bestId: string | null = null;
  let bestPrecedence = Number.POSITIVE_INFINITY;
  let bestRegIndex = Number.POSITIVE_INFINITY;
  let bestBypassEligibility = false;

  const now = Date.now();
  for (const [id, req] of state.pendingRequests.entries()) {
    if (req.retryAfter && now < req.retryAfter) {
      // Skip until cooldown expires
      // eslint-disable-next-line no-continue
      continue;
    }
    const t = state.tooltips.get(id);
    if (t) {
      const isEligible = selectIsEligible(state, t, {
        bypassEligibility: req.bypassEligibility,
      });
      if (isEligible) {
        const precedence = t.config.precedence ?? 0;
        const regIndex =
          state.registrationIndexById.get(id) ?? Number.MAX_SAFE_INTEGER;
        const isBetter =
          precedence < bestPrecedence ||
          (precedence === bestPrecedence && regIndex < bestRegIndex);
        if (isBetter) {
          bestId = id;
          bestPrecedence = precedence;
          bestRegIndex = regIndex;
          bestBypassEligibility = req.bypassEligibility;
        }
      }
    }
  }

  if (!bestId) return state;

  // For multi-step groups, set currentStepId to the first step
  const bestTooltip = state.tooltips.get(bestId);
  let currentStepId = state.currentStepId;
  if (bestTooltip?.config.multiStepGroupId) {
    const groupId = bestTooltip.config.multiStepGroupId;
    const stepIds = state.multiStepGroups.get(groupId);
    if (stepIds && stepIds.length > 0) {
      currentStepId = stepIds[0];
    }
  }

  // Returns the best candidate
  const nextPending = new Map(state.pendingRequests);
  nextPending.delete(bestId);
  return {
    ...state,
    pendingRequests: nextPending,
    activeTooltipId: bestId,
    activeTooltipBypassEligibility: bestBypassEligibility,
    currentStepId,
  };
}
